import React, { memo, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { Howl } from 'howler';

import { getIsSafari } from '@/shared/utils/browser';
import * as playButtonActions from '../store/actions';

import Button from '@/shared/components/button/Button';

import type { PlayButtonState, RequestData, SongData } from '../types';

interface ExtendedHowl extends Howl {
	changeSrc?: (newSrc: string) => void;
	_src?: string | string[];
}

interface PlayButtonOwnProps {
	className?: string;
	colorBtn?: string;
	disabled?: boolean;
	id: string | number;
	lang: string;
	word: string;
}

interface PlayButtonStateProps {
	songId?: string | number | null;
	song?: SongData | null;
	requestData?: RequestData | null;
	shouldHighlight?: boolean;
	paused: boolean;
	isAudioPlaying: boolean;
	loading: boolean;
}

interface PlayButtonDispatchProps {
	getSong: typeof playButtonActions.getSong;
	removeSong: typeof playButtonActions.removeSong;
	setIsAudioPlaying: typeof playButtonActions.setIsAudioPlaying;
	setSongId: typeof playButtonActions.setSongId;
}

type PlayButtonProps = PlayButtonOwnProps & PlayButtonStateProps & PlayButtonDispatchProps;

const PlayButton = ({
	className = '',
	colorBtn = '',
	disabled,
	getSong,
	id,
	lang,
	paused,
	removeSong,
	requestData,
	shouldHighlight,
	song,
	songId,
	word,
	setIsAudioPlaying,
	isAudioPlaying,
	loading,
	setSongId,
}: PlayButtonProps) => {
	window.Howler.autoUnlock = true;
	window.Howler.autoSuspend = false;
	window.Howler.volume(1);

	const [audioFile, setAudioFile] = useState<ExtendedHowl | null>(null);
	const isHighlightState = songId && id && songId.toString() === id.toString();

	useEffect(() => {
		return () => {
			removeSong();
			setSongId(null);
		};
	}, []);

	useEffect(() => {
		if (!audioFile) return;

		audioFile.changeSrc = function (this: ExtendedHowl, newSrc: string) {
			const self = this;
			if (self._src?.[0] !== newSrc) {
				self.unload();
				self._src = newSrc;
				self.load();
			}
		};

		if (song) {
			audioFile.changeSrc(song.url);
			audioFile.play();
		}
	}, [audioFile]);

	useEffect(() => {
		if (song && song.url && isHighlightState) {
			const audio: ExtendedHowl = new Howl({
				src: [song ? song.url : ''],
				html5: true,
				preload: true,
				onplay: () => {
					setIsAudioPlaying(true, true);
				},
				onpause: () => {
					setIsAudioPlaying(false);
				},
				onstop: () => {
					setIsAudioPlaying(false, true);
				},
				onend: () => {
					setIsAudioPlaying(false);
				},
			});
			setAudioFile(audio);
		}
	}, [song]);

	useEffect(() => {
		if (!audioFile) return;
		if (song && song.url && songId?.toString() === id.toString()) {
			audioFile.pause();
		}
	}, [paused]);

	const playWord = (e: React.MouseEvent<HTMLButtonElement>) => {
		window.Howler.stop();
		e.stopPropagation();
		e.currentTarget.blur();

		setSongId(id);

		if (song && song.url && songId?.toString() === id.toString()) {
			audioFile?.pause();
		}

		if (requestData && requestData.word === word && requestData.lang === lang && song) {
			if (getIsSafari()) {
				getSong(id, word, lang, true);
			} else if (audioFile) {
				audioFile.pause();
				audioFile.changeSrc?.(song.url);
				word ? audioFile.play() : getSong(id, word, lang, true);
			} else {
				getSong(id, word, lang, true);
			}
		} else {
			getSong(id, word, lang, true);
		}
	};

	return (
		<Button
			color={colorBtn}
			className={classNames(
				'play-btn',
				{
					active: isAudioPlaying && shouldHighlight && isHighlightState,
				},
				className,
			)}
			iconName="icon-volume-on"
			type="icon-link"
			onClick={playWord}
			disabled={disabled}
			dataName="play_btn"
			loading={Boolean(!isAudioPlaying && loading && shouldHighlight && isHighlightState)}
		/>
	);
};

interface RootState {
	playButtonReducer: PlayButtonState;
}

function mapStateToProps({ playButtonReducer }: RootState): PlayButtonStateProps {
	const { songId, song, requestData, shouldHighlight, paused, isAudioPlaying, loading } = playButtonReducer;

	return {
		songId,
		song,
		requestData,
		shouldHighlight,
		paused,
		isAudioPlaying,
		loading,
	};
}

const mapDispatchToProps = {
	getSong: playButtonActions.getSong,
	removeSong: playButtonActions.removeSong,
	setIsAudioPlaying: playButtonActions.setIsAudioPlaying,
	setSongId: playButtonActions.setSongId,
};

export default connect(mapStateToProps, mapDispatchToProps)(memo(PlayButton));
